import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import DialogComponent from 'components/Dialog';
import {DialogTypes} from 'common/names';
import i18n from '../../../../../i18n';
import Panel from '../../../../../components/Panel';

import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions, layoutSelectors} from '../../../redux';
import Button from '../../../../../components/Button';
import {Trans} from 'react-i18next';
import ConfirmationWrapper from '../../../../../components/buttons/ConfirmationWrapper';
import AddEditUserDialog from './AddEditUserDialog';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {GridColDef, DataGridPro, useGridApiRef} from '@mui/x-data-grid-pro';
import {Chip, Grid} from '@mui/material';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {DIPLOMAT, LDAP} from 'common/constants';
import {settingsActions, settingsSelectors} from 'redux/settings';
import {IAdminRole} from 'types/diplomat/settings/adminroles/AdminRole';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';
import {PasswordPolicies} from 'features/layout/DialogRoutes/dialogs/UserAdmins/PasswordPolicies';
import {AuthPolicies} from 'features/layout/DialogRoutes/dialogs/UserAdmins/AuthPolicies';

const UserAccounts = () => {
   const [selected, setSelected] = useState<IDiplomatAdminUser | null>();
   const [isAddOpened, addOpen] = useState(false);
   const [isEditOpened, editOpen] = useState(false);

   const dispatch = useDispatch();
   const admins = useSelector(layoutSelectors.admins);
   const tableRef = useGridApiRef();

   const rolesRef = useRef<IAdminRole[]>();
   rolesRef.current = useSelector(settingsSelectors.adminRoles);
   React.useEffect(() => {
      dispatch(settingsActions.fetchAdminRoles);
   }, [dispatch]);

   const cols: GridColDef[] = [
      {field: 'contactName', headerName: i18n.t('Contact Name'), flex: 1, resizable: true},
      {
         field: 'adminRoleIds',
         headerName: i18n.t('Roles'),
         flex: 1,
         resizable: true,
         renderCell(params) {
            return (params.value as string[])
               ?.map((roleId) => rolesRef.current?.find((r) => r.id === roleId)?.name)
               .sort()
               .map((role) => <Chip key={role} label={role} />);
         },
      },
      {field: 'userName', headerName: i18n.t('Username'), flex: 1},
      {
         field: 'ldapPasswordAuthentication',
         headerName: i18n.t('Type'),
         flex: 1,
         resizable: true,
         valueFormatter: (params) => (params.value ? LDAP : DIPLOMAT),
      },
      // {field: 'ruserIdole', headerName: i18n.t('User ID'), flex: 1},
      // {
      //    field: 'twoFactorAuthentication',
      //    headerName: i18n.t('Two-Factor\nAuthentication\nRequired'),
      //    flex: 1,
      // },
   ];

   useEffect(() => {
      dispatch(layoutActions.loadUserAdmins());
   }, [dispatch]);

   const add = useCallback(
      (admin: IDiplomatAdminUser, formikBag: FormOnSubmitHelpers<IDiplomatAdminUser>) =>
         dispatch(
            layoutActions.addUserAdmin(admin, {
               onSuccess: () => {
                  addOpen(false);
                  dispatch(layoutActions.loadUserAdmins());
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, addOpen]
   );
   const edit = useCallback(
      (admin: IDiplomatAdminUser, formikBag: FormOnSubmitHelpers<IDiplomatAdminUser>) =>
         dispatch(
            layoutActions.editUserAdmin(admin, {
               onSuccess: () => {
                  editOpen(false);
                  dispatch(layoutActions.loadUserAdmins());
                  setSelected(null);
                  tableRef.current.setSelectionModel([]);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, editOpen, setSelected, tableRef]
   );
   const deleteAdmin = useCallback(
      (id: string) => {
         dispatch(
            layoutActions.deleteUserAdmin(id, {
               onSuccess: () => dispatch(layoutActions.loadUserAdmins()),
            })
         );
      },
      [dispatch]
   );

   return (
      <Panel name={i18n.t('User Accounts')}>
         <div style={{height: 350, width: '100%'}}>
            <DataGridPro
               apiRef={tableRef}
               rows={admins}
               columns={cols}
               pageSize={4}
               onSelectionModelChange={(selectionModel) =>
                  setSelected(admins.find((a) => a.id === selectionModel[0]) as IDiplomatAdminUser)
               }
            />
         </div>
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <PrimaryButton onClick={() => addOpen(true)}>
                  <Trans>Add</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <PrimaryButton onClick={() => editOpen(true)} disabled={!selected}>
                  <Trans>Edit</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <ConfirmationWrapper
                  onOk={() => selected && deleteAdmin(selected.id)}
                  disabled={!selected}
               >
                  <Button color="secondary" disabled={!selected}>
                     <Trans>Delete</Trans>
                  </Button>
               </ConfirmationWrapper>
            </Grid>
         </Grid>

         {isAddOpened ? <AddEditUserDialog onSubmit={add} onClose={() => addOpen(false)} /> : null}
         {isEditOpened && selected ? (
            <AddEditUserDialog user={selected} onSubmit={edit} onClose={() => editOpen(false)} />
         ) : null}
      </Panel>
   );
};

type Props = {
   onClose: () => void;
};

const UserAccountSettingDialog: ReactFChildrenProps<Props> = memo((props) => {
   const {onClose} = props;

   return (
      <DialogComponent
         maxWidth="lg"
         header={DialogTypes.UserAccountSettingDialog.dialogName}
         close={onClose}
      >
         <UserAccounts />
         <AuthPolicies />
         <PasswordPolicies />
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <Button type="button" onClick={onClose}>
                  <Trans>Close</Trans>
               </Button>
            </Grid>
         </Grid>
      </DialogComponent>
   );
});

export default UserAccountSettingDialog;
